import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { FiDateFormatOptions, FiDateTimeFormatOptions, getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { ChecklistViewerStatus } from '@fi-shared/checklist/fi-checklist-viewer-status';
import { IChecklist2UnitWithPeriodModel, IChecklistV2Viewer } from '@fi-shared/checklist/checklist-v2';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiChecklist2ScheduleService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule.service';
import { FiToggleSwitchLabelAlign } from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { FiUserDataService } from '@fi-app/shared/fi-shared-services/fi-user-data.service';
import { FiUserSharedService } from '@fi-shared/fi-user-shared/fi-user-shared.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiChecklistViewerSnackbarColorClass } from '@fi-shared/checklist/fi-checklist2-viewer/disabled-viewer-warning-snackbar/disabled-viewer-warning-snackbar.component';
import { ObjectTypes } from '@fi-shared/types';
import { PublicationItemStatus } from '@fi-shared/fi-edit-publication/publication-item-status';
import { FiChecklist2ScheduleDialogService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-dialog.service';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';

@Component({
    selector: 'fi-checklist2-viewer-status',
    templateUrl: './viewer-status.component.html',
    styleUrls: ['./viewer-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-viewer-status' },
    standalone: false,
})
export class FiChecklist2ViewerStatusComponent {
    @Input() checklistViewer: IChecklistV2Viewer;
    @Input() viewerStatus: ChecklistViewerStatus;
    @Input() isViewerActive: boolean;
    @Input() hasAccessToToggleApplicable: boolean;
    @Input() stateBusinessUnitId: number;
    @Input() checklistUnitsSchedulePeriods: IChecklist2UnitWithPeriodModel[] = [];
    @Input({ required: true }) auditMode: boolean;
    @Input() isPreviewMode: boolean;
    @Input() fromLinkTo: boolean;
    @Output() toggleNotApplicable = new EventEmitter<void>();
    @Output() reloadViewerState = new EventEmitter();

    constructor(
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiChecklistService: FiChecklist2Service,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
        private fiChecklist2ScheduleService: FiChecklist2ScheduleService,
        private fiChecklist2ScheduleDialogService: FiChecklist2ScheduleDialogService,
        private fiUserDataService: FiUserDataService,
        private fiUserSharedService: FiUserSharedService,
        private fiStateService: FiStateService,
    ) {}

    isAuditOnDemandSharingSettings(): boolean {
        return (
            this.isOnDemandOrAuditChecklistReadyToStart() &&
            !this.isOnDemandScheduleNotSharedWithUnit() &&
            !this.isViewerActive
        );
    }

    private canToggleNotApplicable(): boolean {
        // text: This checklist is not applicable for this unit
        // action: Make applicable
        return this.hasAccessToToggleApplicable && this.viewerStatus === ChecklistViewerStatus.NotApplicable;
    }

    private canScheduleNotScheduled(): boolean {
        // not "from link to" case
        // text: This audit checklist is not scheduled.
        // action: Schedule
        return (
            this.fiChecklistHelperService.isChecklistNotScheduled(this.checklistViewer) &&
            !this.checklistViewer.DisableChecklistSchedule &&
            !this.fromLinkTo &&
            this.fiChecklistService.isChecklistShareRights(this.auditMode)
        );
    }

    private canGoToModuleForNotScheduledLinkTo(): boolean {
        // "from link to" case
        // text: No connected schedule is found from this checklist link. Please go to Checklists 2.0 module to schedule this checklist or find its connected schedule.
        // action: Checklists 2.0
        return this.fiChecklistHelperService.isChecklistNotScheduled(this.checklistViewer) && this.fromLinkTo;
    }

    isSnackbarActionButtonShown(): boolean {
        return (
            this.canToggleNotApplicable() || this.canScheduleNotScheduled() || this.canGoToModuleForNotScheduledLinkTo()
        );
    }

    getSnackbarActionMessage(): string {
        if (this.canToggleNotApplicable()) return this.appTextService.get('checklist.make_applicable');
        if (this.canScheduleNotScheduled()) return this.appTextService.get('checklist.schedule');
        if (this.canGoToModuleForNotScheduledLinkTo()) {
            const checklistsNameTextId: string = getObjectTypeNameTextId(
                this.auditMode ? ObjectTypes.Checklist2Audit : ObjectTypes.Checklist2,
                true,
            );
            return this.appTextService.get(checklistsNameTextId);
        }
    }

    onSnackbarActionClick() {
        if (this.canToggleNotApplicable()) {
            this.toggleNotApplicable.emit();
        } else if (this.canScheduleNotScheduled()) {
            this.fiChecklist2ScheduleDialogService.openAddChecklistScheduleDialogWithChecklistId(
                this.checklistViewer.Checklist.Id,
                this.auditMode,
            );
        } else if (this.canGoToModuleForNotScheduledLinkTo()) {
            const rootState = FiStateNames.getChecklistV2RootState(this.auditMode);
            this.fiStateService.go(rootState.List);
        }
    }

    isChecklistOpenHoursShown(): boolean {
        return (
            (this.isOnDemandOrAuditChecklistReadyToStart() && this.isViewerActive) ||
            (!this.isOnDemandOrAuditChecklistReadyToStart() && this.isPreviewMode) ||
            (!this.isPreviewMode && this.viewerStatus === ChecklistViewerStatus.Active) ||
            (this.viewerStatus === ChecklistViewerStatus.ScheduleNotSharedToBusinessUnit &&
                this.isChecklistNotSharedToBusinessUnitAvailable(this.checklistUnitsSchedulePeriods.length))
        );
    }

    isScheduleNotSharedWithUnit(): boolean {
        return (
            (this.isOnDemandOrAuditChecklistReadyToStart() &&
                this.isOnDemandScheduleNotSharedWithUnit() &&
                this.isOnDemandChecklistNotSharedToBusinessUnitAvailable()) ||
            (!this.isOnDemandOrAuditChecklistReadyToStart() &&
                !this.isPreviewMode &&
                this.viewerStatus === ChecklistViewerStatus.ScheduleNotSharedToBusinessUnit &&
                this.isChecklistNotSharedToBusinessUnitAvailable(this.checklistUnitsSchedulePeriods.length))
        );
    }

    isAuditOnDemandUnitSelector(): boolean {
        return (
            this.isOnDemandOrAuditChecklistReadyToStart() && this.isViewerActive && this.isAuditAvailableExternalUnit()
        );
    }

    isViewerDisabledBannerShown(): boolean {
        const result =
            !this.isOnDemandOrAuditChecklistReadyToStart() &&
            !this.isPreviewMode &&
            !this.checklistViewer.Checklist.TemplateMode &&
            this.viewerStatus !== ChecklistViewerStatus.Active &&
            !this.isScheduleNotSharedWithUnit();
        return result;
    }

    isOperationalPeriodActive(): boolean {
        return this.fiChecklistHelperService.isOperationalPeriodActive(this.checklistViewer);
    }

    isChecklistAvailableLater(): boolean {
        return this.fiChecklistHelperService.isChecklistAvailableLater(this.checklistViewer);
    }

    isOnDemandOrAuditChecklistReadyToStart(): boolean {
        const notReadyStatuses: ChecklistViewerStatus[] = [
            ChecklistViewerStatus.OperationalNotStarted,
            ChecklistViewerStatus.Ended,
            ChecklistViewerStatus.NotOperational,
        ];
        return (
            (this.isAuditSchedule() || this.isOnDemandSchedule()) &&
            !this.isPreviewMode &&
            !notReadyStatuses.includes(this.viewerStatus) &&
            this.checklistViewer.HasAccessToSchedule &&
            this.checklistUnitsSchedulePeriods.length > 0
        );
    }

    isAuditSchedule(): boolean {
        return this.fiChecklistHelperService.isAuditSchedule(this.checklistViewer.ScheduleType) /* &&
            this.checklistViewer.HasEditAccessToSchedule*/;
    }

    isOnDemandSchedule(): boolean {
        return this.fiChecklistHelperService.isOnDemandSchedule(this.checklistViewer.ScheduleType) /* &&
            this.checklistViewer.HasEditAccessToSchedule*/;
    }

    createPeriod(businessUnitId: number) {
        if (this.isOnDemandOrAuditChecklistReadyToStart() && !isDefined(businessUnitId))
            businessUnitId = this.fiUserDataService.getUserData().BusinessUnitId;

        this.fiChecklist2ScheduleService.createPeriod(this.checklistViewer.ScheduleId, businessUnitId).then(() => {
            this.reloadViewerState.emit(businessUnitId);
        });
    }

    isAuditAvailableExternalUnit(): boolean {
        return (
            isDefined(this.stateBusinessUnitId) && this.stateBusinessUnitId !== 0 /* &&
            this.stateBusinessUnitId !== this.fiUserDataService.getUserData().BusinessUnitId*/
        ); // commented out: item 3 https://chainformation.atlassian.net/browse/FI-6091
    }

    changeUsersBusinessUnit(businessUnit: INameIdObj) {
        this.fiUserSharedService.saveUserPreferredBusinessUnit(businessUnit);
    }

    getViewerSnackbarText(): string {
        const checklistScheduleTextId: string = this.appTextService.get(
            getObjectTypeNameTextId(
                this.auditMode ? ObjectTypes.Checklist2AuditSchedule : ObjectTypes.ChecklistSchedule,
            ),
        );
        const checklistName: string = getObjectTypeNameTextId(
            this.auditMode ? ObjectTypes.Checklist2Audit : ObjectTypes.Checklist2,
        );

        if (this.viewerStatus === ChecklistViewerStatus.NotScheduled) {
            if (this.fromLinkTo) {
                const checklistsName: string = getObjectTypeNameTextId(
                    this.auditMode ? ObjectTypes.Checklist2Audit : ObjectTypes.Checklist2,
                    true,
                );
                return this.appTextService.get('checklist.checklist_not_scheduled_from_link_to', {
                    '{0}': this.appTextService.get(checklistsName),
                });
            }

            return this.appTextService.get('checklist.checklist_not_scheduled', {
                '{0}': this.appTextService.get(checklistName).toLowerCase(),
            });
        }
        if (this.viewerStatus === ChecklistViewerStatus.AvailableLater) {
            return this.appTextService.get('checklist.available_from', {
                '{0}': this.fiFormatDateService.formatDate(
                    this.checklistViewer.StartDateTimeStr,
                    FiDateTimeFormatOptions,
                    { showToday: false, showUTC: true },
                ),
            });
        }
        if (this.viewerStatus === ChecklistViewerStatus.Ended) {
            if (this.checklistViewer.OperationalPeriodStatus === PublicationItemStatus.Ended)
                return this.appTextService.get('checklist.schedule_expired', {
                    '{0}': checklistScheduleTextId.toLowerCase(),
                });

            return this.appTextService.get('checklist.already_ended');
        }
        if (
            this.viewerStatus === ChecklistViewerStatus.ScheduleNotShared ||
            this.viewerStatus === ChecklistViewerStatus.ScheduleNotSharedToBusinessUnit
        ) {
            return this.appTextService.get(
                this.auditMode ? 'checklist.audit_schedule_not_shared' : 'checklist.schedule_not_shared',
            );
        }
        if (this.viewerStatus === ChecklistViewerStatus.NotApplicable) {
            return this.appTextService.get('checklist.not_applicable_status_header');
        }
        if (this.viewerStatus === ChecklistViewerStatus.OperationalNotStarted) {
            if (this.isAuditSchedule() || this.isOnDemandSchedule()) {
                return this.appTextService.get('checklist.operational_from', {
                    '{0}': this.fiFormatDateService.formatDate(
                        this.checklistViewer.OperationalStartDateStr,
                        FiDateFormatOptions,
                        { showToday: false },
                    ),
                });
            }
            return this.appTextService.get('checklist.not_operational', {
                '{0}': this.appTextService.get(checklistScheduleTextId).toLowerCase(),
            });
        }
        if (this.viewerStatus === ChecklistViewerStatus.NotOperational) {
            return this.appTextService.get('checklist.not_operational', {
                '{0}': this.appTextService.get(checklistScheduleTextId).toLowerCase(),
            });
        }
        if (this.viewerStatus === ChecklistViewerStatus.Unknown) {
            return this.appTextService.get('form.not_available');
        }

        throw new Error(`getViewerDisabledText: unknown viewerStatus ${this.viewerStatus}`);
    }

    getViewerSnackbarIconClass(): string {
        if (this._operationalIconViewerStatus.includes(this.viewerStatus))
            return this._operationalInfoSnackbarIconClass;

        if (this._notOperationalIconViewerStatus.includes(this.viewerStatus))
            return this._notOperationalInfoSnackbarIconClass;

        if (this._infoIconViewerStatus.includes(this.viewerStatus)) return this._infoViewerSnackbarIconClass;

        return this._defaultViewerSnackbarIconClass;
    }

    getViewerSnackbarColorClass(): FiChecklistViewerSnackbarColorClass {
        return this._standardColorClassViewerStatus.includes(this.viewerStatus)
            ? FiChecklistViewerSnackbarColorClass.Standard
            : FiChecklistViewerSnackbarColorClass.Error;
    }

    isOnDemandScheduleNotSharedWithUnit(): boolean {
        return this.viewerStatus === ChecklistViewerStatus.ScheduleNotSharedToBusinessUnit && this.isOnDemandSchedule();
    }

    isOnDemandChecklistNotSharedToBusinessUnitAvailable(): boolean {
        return !!this.checklistUnitsSchedulePeriods.length;
    }

    isChecklistNotSharedToBusinessUnitAvailable(checklistUnitsSchedulePeriodsCount: number): boolean {
        return this.isOperationalPeriodActive() && !!checklistUnitsSchedulePeriodsCount;
    }

    isAuditOnDemandDueDateSoon(): boolean {
        return this.fiChecklistHelperService.isAuditOnDemandDueDateSoon(this.checklistViewer);
    }

    isDueDateSoon(): boolean {
        return this.fiChecklistHelperService.isTimeLessThenDay(this.checklistViewer.DueDateTimeStr);
    }

    protected readonly FiDateTimeFormatOptions = FiDateTimeFormatOptions;
    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;

    private readonly _operationalIconViewerStatus: readonly ChecklistViewerStatus[] = [
        ChecklistViewerStatus.AvailableLater,
        ChecklistViewerStatus.OperationalNotStarted,
    ];

    private readonly _notOperationalIconViewerStatus: readonly ChecklistViewerStatus[] = [
        ChecklistViewerStatus.NotOperational,
        ChecklistViewerStatus.Ended,
    ];

    private readonly _infoIconViewerStatus: readonly ChecklistViewerStatus[] = [
        ChecklistViewerStatus.ScheduleNotShared,
        ChecklistViewerStatus.ScheduleNotSharedToBusinessUnit,
        ChecklistViewerStatus.NotScheduled,
    ];

    private readonly _standardColorClassViewerStatus: readonly ChecklistViewerStatus[] = [
        ChecklistViewerStatus.ScheduleNotShared,
        ChecklistViewerStatus.ScheduleNotSharedToBusinessUnit,
        ChecklistViewerStatus.OperationalNotStarted,
        ChecklistViewerStatus.NotOperational,
        ChecklistViewerStatus.Ended,
        ChecklistViewerStatus.NotScheduled,
        ChecklistViewerStatus.AvailableLater,
    ];

    private readonly _defaultViewerSnackbarIconClass: string = 'fas fa-eye-slash';
    private readonly _infoViewerSnackbarIconClass: string = 'far fa-circle-info';
    private readonly _notOperationalInfoSnackbarIconClass: string = 'far fa-calendar-xmark';
    private readonly _operationalInfoSnackbarIconClass: string = 'far fa-calendar-day';
}
